import React from 'react';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../config/api.config';

/**
 * Shared hook for discount management functionality
 * Works with both ExperimentPanel and ProductExperimentPanelNew via data adapters
 */
export const useDiscountManagement = (experimentsData, dataAdapter, clientId = null, onDataRefresh = null) => {
  const [customDiscounts, setCustomDiscounts] = React.useState({});
  const [discountInputs, setDiscountInputs] = React.useState({});
  const [pendingSaves, setPendingSaves] = React.useState({});
  const [pendingCombos, setPendingCombos] = React.useState({});
  const [apiResponseOverrides, setApiResponseOverrides] = React.useState({});
  const [updatedStates, setUpdatedStates] = React.useState({});
  
  // States for custom product selection
  const [showProductSelection, setShowProductSelection] = React.useState({});
  const [customProductSelections, setCustomProductSelections] = React.useState({});
  // { pricing, apply } — un experimento ya agendado contra el ERP no puede cambiar de producto
  // o de % sin reprogramar precios, así que el operador los confirma antes de guardar.
  const [priceReview, setPriceReview] = React.useState(null);

  // Helper function to calculate discounted price
  const calculateDiscountedPrice = (originalPrice, discountPercent) => {
    const price = originalPrice || 0;
    const discount = discountPercent || 0;
    return price * (1 - (discount / 100));
  };

  // Helper function to get original discount percentage from experiment data
  const getOriginalDiscountPercent = (item, selectedCombo) => {
    const experiment = dataAdapter.getExperiment(item);
    if (!experiment?.comboPrices || !selectedCombo?.name) return 0;
    
    const comboData = experiment.comboPrices.find(c => c.candidateName === selectedCombo.name);
    return comboData ? (comboData.discount || 0) : 0; // Use API discount directly with fallback
  };

  // Helper function to get effective discount (custom or original)
  const getEffectiveDiscount = (productKey, item, selectedCombo) => {
    if (customDiscounts[productKey] !== undefined) {
      return customDiscounts[productKey];
    }
    return getOriginalDiscountPercent(item, selectedCombo);
  };

  // Handle discount input change
  const handleDiscountInputChange = (productKey, value, item, getProductData, selectedCombo) => {
    // Update input state immediately for responsive UI
    setDiscountInputs(prev => ({ ...prev, [productKey]: value }));
    
    // Show save button when user has modified the discount
    const effectiveCombo = selectedCombo || getProductData[productKey]?.candidates[0];
    const originalDiscount = getOriginalDiscountPercent(item, effectiveCombo);
    
    const numValue = parseFloat(value);
    const hasChanged = !isNaN(numValue) && numValue !== originalDiscount && value !== '';
    
    // Show save button for changes (unified with combo changes)
    setPendingSaves(prev => ({ ...prev, [productKey]: hasChanged || pendingCombos[productKey] }));
    
    // Update custom discount if valid
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setCustomDiscounts(prev => ({ ...prev, [productKey]: numValue }));
    }
  };

  // Handle discount reset (double-click)
  const handleDiscountReset = (productKey, item, getProductData, selectedCombo) => {
    const effectiveCombo = selectedCombo || getProductData[productKey]?.candidates[0];
    const originalDiscount = getOriginalDiscountPercent(item, effectiveCombo);
    
    // Reset to original discount
    setCustomDiscounts(prev => {
      const newState = { ...prev };
      delete newState[productKey];
      return newState;
    });
    setDiscountInputs(prev => ({ ...prev, [productKey]: originalDiscount.toString() }));
    
    // Check if there are still pending combo changes
    setPendingSaves(prev => ({ ...prev, [productKey]: !!pendingCombos[productKey] }));
  };

  // Handle unified save click (combo + discount changes)
  const handleSaveChanges = async (productKey, item, getProductData, selectedCombo, directCustomSelection = null) => {
    const experiment = dataAdapter.getExperiment(item);
    if (!experiment?.id) return;

    try {
      // Check if we have a custom product selection (direct or from state)
      const customSelection = directCustomSelection || customProductSelections[productKey];
      let currentCombo, feedback;
      
      if (customSelection) {
        // Use custom product selection
        currentCombo = {
          name: customSelection.productName,
          price: '0.00',
          candidateId: customSelection.productId
        };
        feedback = customSelection.reason;
      } else {
        // Get current combo (pending or existing)
        currentCombo = pendingCombos[productKey] || selectedCombo || getProductData[productKey]?.candidates[0];
      }
      
      // Get current discount (custom or original)
      const currentDiscount = customDiscounts[productKey] !== undefined ? customDiscounts[productKey] : getOriginalDiscountPercent(item, currentCombo);
      
      // Clear pending states
      setPendingSaves(prev => ({ ...prev, [productKey]: false }));
      setPendingCombos(prev => {
        const newState = { ...prev };
        delete newState[productKey];
        return newState;
      });
      
      // Clear custom product selection after save
      if (customSelection) {
        setCustomProductSelections(prev => {
          const newState = { ...prev };
          delete newState[productKey];
          return newState;
        });
      }
      
      // Show updated indicator
      setUpdatedStates(prev => ({ ...prev, [productKey]: true }));
      setTimeout(() => {
        setUpdatedStates(prev => ({ ...prev, [productKey]: false }));
      }, 3000);
      
      // Update API response overrides to reflect new values
      const comboData = experiment.comboPrices.find(c => c.candidateName === currentCombo.name);
      if (comboData) {
        const newDiscountedPrice = calculateDiscountedPrice(comboData.totalWithoutDiscount, currentDiscount);
        const overrideKey = `${productKey}-${currentCombo.name}`;
        
        setApiResponseOverrides(prev => ({
          ...prev,
          [overrideKey]: {
            price: newDiscountedPrice,
            discount: currentDiscount
          }
        }));
      }
      
      // Call API to save both combo and discount
      await handleComboUpdate(experiment.id, currentCombo, productKey, currentDiscount, experiment, feedback, null, null, customSelection?.price);
      
    } catch (error) {
      // Revert on error
      setCustomDiscounts(prev => {
        const newState = { ...prev };
        delete newState[productKey];
        return newState;
      });
      setPendingCombos(prev => {
        const newState = { ...prev };
        delete newState[productKey];
        return newState;
      });
    }
  };

  // API call to update combo and discount
  const handleComboUpdate = async (experimentId, newCombo, productKey, customDiscount = null, experiment, feedback = null, beginDate = null, endDate = null, customProductPrice = null, pricingOverride = null) => {
    try {
      let candidateId;
      
      // Check if this is a custom product selection
      if (newCombo.candidateId) {
        candidateId = newCombo.candidateId;
      } else {
        // Original flow - find candidate in existing arrays
        const candidateIndex = experiment.candidateNames.indexOf(newCombo.name);
        candidateId = experiment.candidateProductIds[candidateIndex];
      }
      
      // Use custom discount if provided, otherwise use original discount
      const discountToUse = customDiscount !== null ? customDiscount : 
        (newCombo.candidateId ? 0 : experiment.candidateDiscounts[experiment.candidateNames.indexOf(newCombo.name)]);
      
      // Get combo price and cost from experiment comboPrices
      const comboData = experiment.comboPrices.find(c => c.candidateName === newCombo.name);
      const comboPrice = comboData ? comboData.totalWithoutDiscount : undefined;
      const comboCost = comboData ? comboData.totalCost : undefined;
      
      const requestBody = { 
        candidateProductId: candidateId,
        discount: discountToUse,
        clientId: clientId || experiment.clientId || experiment.client_id,
        price: comboPrice,
        cost: comboCost,
        experimentType: experiment.experimentType
      };

      // Add feedback if provided (for custom product selections)
      if (feedback) {
        requestBody.feedback = feedback;
        
        // Add custom product price if provided
        if (customProductPrice) {
          requestBody.customProductPrice = customProductPrice;
        }
      }

      // Add date parameters if provided
      if (beginDate) requestBody.beginDate = beginDate;
      if (endDate) requestBody.endDate = endDate;

      const token = localStorage.getItem('access_token');

      // Ya agendado contra el ERP: primero se enseñan los precios que quedarían tras el cambio
      // y sólo se guarda con los que el operador confirme.
      if (['accepted', 'running'].includes(experiment.status) && !pricingOverride) {
        const preview = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${encodeURIComponent(requestBody.clientId)}`,
          {
            experimentId,
            newDProductId: candidateId,
            discount: discountToUse,
            ignoreOrchestratorTarget: true,
            // Producto nuevo tecleado a mano: el precio que puso el operador manda sobre
            // cualquier precio histórico del producto (puede no tener ninguno en esa máquina).
            ...(customProductPrice ? { targetPriceHint: Number(customProductPrice) } : {}),
          },
          { headers: createAuthHeader(token) }
        );
        if (preview.data?.pricing) {
          setPriceReview({
            pricing: preview.data.pricing,
            apply: (override) => handleComboUpdate(
              experimentId, newCombo, productKey, customDiscount, experiment,
              feedback, beginDate, endDate, customProductPrice, override
            ),
          });
          return;
        }
      }
      if (pricingOverride) requestBody.pricingOverride = pricingOverride;

      const response = await axios.post(
        `${API_BASE_URL}/experiments/${experimentId}/candidate`,
        requestBody,
        { headers: createAuthHeader(token) }
      );
      
      // Trigger data refresh after successful combo update
      if (onDataRefresh) {
        await onDataRefresh();
      }
    } catch (error) {
      // Handle combo update error silently
    }
  };

  // Handle combo selection changes
  const handleComboSelect = (productKey, candidate, item) => {
    // Update discount input to show new candidate's discount
    const newCandidateDiscount = getOriginalDiscountPercent(item, candidate);
    setDiscountInputs(prev => ({ ...prev, [productKey]: newCandidateDiscount.toString() }));
    
    // Clear any custom discount since we're switching candidates
    setCustomDiscounts(prev => {
      const newState = { ...prev };
      delete newState[productKey];
      return newState;
    });
    
    // Track pending combo change
    setPendingCombos(prev => {
      return { ...prev, [productKey]: candidate };
    });
    
    // Show save button for combo changes
    setPendingSaves(prev => {
      return { ...prev, [productKey]: true };
    });
  };


  // Handle "Select a new product" click
  const handleSelectNewProduct = (productKey) => {
    // Show product selection UI
    setShowProductSelection(prev => {
      const newState = {};
      newState[productKey] = true;
      return newState;
    });
  };

  // Handle custom product selection - directly save
  const handleCustomProductSelect = async (productKey, productId, productName, reason, price, item, getProductData, selectedCombo) => {
    // Hide product selection UI immediately
    setShowProductSelection(prev => ({
      ...prev,
      [productKey]: false
    }));
    
    // Prepare custom selection data
    const customSelection = { productId, productName, reason, price };
    
    // Call save directly with custom selection data
    await handleSaveChanges(productKey, item, getProductData, selectedCombo, customSelection);
  };

  // Cancel product selection
  const handleCancelProductSelection = (productKey) => {
    setShowProductSelection(prev => ({
      ...prev,
      [productKey]: false
    }));
  };

  return {
    // State
    customDiscounts,
    discountInputs,
    pendingSaves,
    pendingCombos,
    apiResponseOverrides,
    updatedStates,
    showProductSelection,
    customProductSelections,
    priceReview,

    // State setters (for external control)
    setCustomDiscounts,
    setDiscountInputs,
    setPendingSaves,
    setPendingCombos,
    setApiResponseOverrides,
    setUpdatedStates,
    
    // Helper functions
    calculateDiscountedPrice,
    getOriginalDiscountPercent,
    getEffectiveDiscount,
    
    // Event handlers
    handleDiscountInputChange,
    handleDiscountReset,
    handleSaveChanges,
    handleComboSelect,
    handleComboUpdate,
    handleSelectNewProduct,
    handleCustomProductSelect,
    handleCancelProductSelection,
    cancelPriceReview: () => setPriceReview(null),
    confirmPriceReview: (pricingOverride) => {
      const review = priceReview;
      setPriceReview(null);
      review?.apply(pricingOverride);
    }
  };
};